import Parse from 'parse';
import { HttpClient } from '@angular/common/http';
import { Provider } from '@angular/core';
import {
  ParseCaseTypeRepository,
  ParseMethodTypeRepository,
  ParsePipelineRepository,
  ParseResourceRepository,
} from '@telespot/infrastructure/parse';
import { LoggerService } from '@telespot/shared/logger/feature/util';
import { HttpFileUploaderStorage } from '@shared/infra/http';
import {
  SaveAssignedPipelinesCommand,
  SaveCaseTypeCommand,
  SaveMethodTypeCommand,
  SavePipelineCommand,
  SaveResourceCommand,
} from '@protocols/use-cases';

import { AssignedMethodTypesState } from '../state/assigned-method-types.state';
import { AssignedPipelinesState } from '../state/assigned-pipelines.state';
import { AssignedResourcesState } from '../state/assigned-resources.state';
import { CaseTypeEditorState } from '../state/case-type-editor.state';
import { MethodTypeEditorState } from '../state/method-type-editor.state';
import { PipelineEditorState } from '../state/pipeline-editor.state';
import { SaveProtocolCascadeState } from '../state/save-protocol-cascade.state';
import { StagedCaseDataState } from '../state/staged-case-data.state';
import { StagedMethodTypesState } from '../state/staged-method-types.state';
import { StagedPipelinesState } from '../state/staged-pipelines.state';
import { StagedResourcesState } from '../state/staged-resources.state';

import { SAVE_LABEL_CONFIG_COMMAND } from './label-config-save.effects';
import { labelConfigSaveProviders } from './label-config-save.providers';
import { LabelConsolidationFailureEffects } from './label-consolidation-failure.effects';
import {
  CASCADE_SAVE_ASSIGNED_PIPELINES_COMMAND,
  CASCADE_SAVE_CASE_TYPE_COMMAND,
  CASCADE_SAVE_LABEL_CONFIG_COMMAND,
  CASCADE_SAVE_METHOD_TYPE_COMMAND,
  CASCADE_SAVE_PIPELINE_COMMAND,
  CASCADE_SAVE_RESOURCE_COMMAND,
  SaveProtocolCascadeEffects,
} from './save-protocol-cascade.effects';

export const saveProtocolCascadeProviders: Provider[] = [
  SaveProtocolCascadeState,
  SaveProtocolCascadeEffects,

  // Failure-event derivation for the label batch + config tail. Same label-tier
  // seam as the cascade itself: injects the per-scope `LabelState` (not
  // provided here) alongside the config tier's `LabelConfigState`.
  LabelConsolidationFailureEffects,

  // The staged-config ledger and its save command, plus the standalone batch for
  // consumers that persist staged configs without a cascade. Shared verbatim.
  ...labelConfigSaveProviders,

  // The cascade drives the same command through its own token, so its tail is a
  // phase it owns rather than a call into the batch class above.
  {
    provide: CASCADE_SAVE_LABEL_CONFIG_COMMAND,
    useExisting: SAVE_LABEL_CONFIG_COMMAND,
  },

  // States the cascade injects (plain no-arg ComponentStores). `LabelState` is
  // not here — see the per-scope label-tier note above.
  PipelineEditorState,
  StagedResourcesState,
  AssignedResourcesState,
  StagedPipelinesState,
  AssignedPipelinesState,
  MethodTypeEditorState,
  StagedMethodTypesState,
  AssignedMethodTypesState,
  StagedCaseDataState,
  CaseTypeEditorState,

  // Save commands — repos inlined, identical wiring on every root.
  {
    provide: CASCADE_SAVE_PIPELINE_COMMAND,
    useFactory: () => new SavePipelineCommand(new ParsePipelineRepository(Parse, { useMasterKey: false })),
  },
  {
    provide: CASCADE_SAVE_RESOURCE_COMMAND,
    useFactory: (http: HttpClient, logger: LoggerService) =>
      new SaveResourceCommand(
        new ParseResourceRepository(Parse, { useMasterKey: false }),
        new HttpFileUploaderStorage(http, logger)
      ),
    deps: [HttpClient, LoggerService],
  },
  {
    provide: CASCADE_SAVE_METHOD_TYPE_COMMAND,
    useFactory: () => new SaveMethodTypeCommand(new ParseMethodTypeRepository(Parse, { useMasterKey: false })),
  },
  {
    provide: CASCADE_SAVE_ASSIGNED_PIPELINES_COMMAND,
    useFactory: () => new SaveAssignedPipelinesCommand(new ParseMethodTypeRepository(Parse, { useMasterKey: false })),
  },
  {
    provide: CASCADE_SAVE_CASE_TYPE_COMMAND,
    useFactory: () => new SaveCaseTypeCommand(new ParseCaseTypeRepository(Parse, { useMasterKey: false })),
  },
];
